import { colors } from '@/constants/colors'
import { EdgeGlass } from '@/ui/core/block'
import { FlowHeader } from '@/ui/core/flow-header'
import { Header } from '@/ui/core/header'
import { Rise } from '@/ui/core/motion'
import { Note, type NoteTone } from '@/ui/core/note'
import { PageShell } from '@/ui/core/page-shell'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Animated, {
	useAnimatedReaction,
	useAnimatedScrollHandler,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'
import Images from '#/assets/images/image-map'
import type { FormProgress } from './logic/navigation'

/**
 * How the step speaks. A `question` is asked in the voice the clinic wrote it in: sentence case,
 * H3, plainly. A `moment` is the form's own — its opening and its closing word — and is set in
 * type instead: the brand's H1 in caps, standing at the foot of the page.
 */
export type StepLead = 'question' | 'moment'

interface StepLayoutProps {
	title: string
	description?: string
	/** The primary button, or nothing for a step that moves on by itself. */
	cta?: ReactNode
	children?: ReactNode
	progress: FormProgress
	lead?: StepLead
	/** The note the answers sit on: blush for fields and rows, cream for text to read. */
	surface?: NoteTone
	/** Absent on the first step. */
	onBack?: () => void
	onClose: () => void
}

/**
 * The frame every step sits in: the flow header with its measure, the question, the answers on a
 * note below it, and the button pinned under them so a long list of conditions never hides the
 * way forward.
 *
 * The answers are on a note because everything in this app is: pink ground, a card, and white
 * elements inside it. Left on the bare ground a field or a row of options has no body, and a
 * flow of twenty-odd of them stops looking like the rest of the app.
 *
 * The note takes the whole middle rather than hugging its contents. Hugging, a single field gets
 * a sixteen-point halo of blush that reads as a rendering fault; filling, the same note is the
 * sheet the patient is writing on, and the composition holds still from the first step to the
 * twenty-third however many answers a step happens to hold.
 *
 * The measure is a bar and not a count. A form's steps are conditional — most of the twenty-odd
 * in the welcome form are skipped for any one patient — so "1 of 23" would both overstate the
 * work and be wrong, while a bar filling as they go is true either way.
 *
 * A step with nothing to answer has no middle to fill, so its words sit centred on the page
 * rather than stranded at the top of an empty one. On desktop the brand photo takes the right
 * half.
 */
/** Whether anything is hidden past the top and the foot of a scroll, for the glass over each. */
const useOverflow = () => {
	const offset = useSharedValue(0)
	const viewport = useSharedValue(0)
	const content = useSharedValue(0)

	const onScroll = useAnimatedScrollHandler((event) => {
		offset.value = event.contentOffset.y
		viewport.value = event.layoutMeasurement.height
		content.value = event.contentSize.height
	})

	// Each edge's opacity is a value of its own, starting at nothing, rather than something the
	// style works out as it draws. A style that returns `withTiming(0)` has nothing to begin from
	// on its first frame and starts at full, so the glass came up opaque and faded off every time
	// a step was drawn: a blur over the first row, which is what the content looked like it was
	// tucking under.
	const topShown = useSharedValue(0)
	const bottomShown = useSharedValue(0)

	useAnimatedReaction(
		() => offset.value > 2,
		(scrolled, was) => {
			if (scrolled !== was)
				topShown.value = withTiming(scrolled ? 1 : 0, { duration: 140 })
		}
	)
	useAnimatedReaction(
		() => content.value - offset.value - viewport.value > 2,
		(more, was) => {
			if (more !== was) bottomShown.value = withTiming(more ? 1 : 0, { duration: 140 })
		}
	)

	const top = useAnimatedStyle(() => ({ opacity: topShown.value }))
	const bottom = useAnimatedStyle(() => ({ opacity: bottomShown.value }))

	return {
		onScroll,
		onLayout: (height: number) => {
			viewport.value = height
		},
		onContent: (height: number) => {
			content.value = height
		},
		top,
		bottom,
	}
}

export const StepLayout = ({
	title,
	description,
	cta,
	children,
	progress,
	lead = 'question',
	surface = 'blush',
	onBack,
	onClose,
}: StepLayoutProps) => {
	const isMoment = lead === 'moment'
	const overflow = useOverflow()

	return (
		<View className="flex-1 bg-ground">
			<FlowHeader onBack={onBack} onClose={onClose} progress={progress.fraction} />
			<PageShell
				photo={Images.BrandPhoto}
				scroll={false}
				// A question wants air over it: it is the first thing on the page and the header's
				// measure sits close above it.
				contentClassName={isMoment ? 'py-6 lg:py-10' : 'pt-10 pb-6 lg:py-10'}
			>
				{/* A moment is its words and the button under them, so the space between the two is
				    the one gap on the screen and is worth more than the twenty a step's note sits
				    at. */}
				<VStack className="flex-1" gap={isMoment ? 44 : 20}>
					{/* Each step's words arrive rather than appear, so moving through the form reads as
					    movement and not as a page reloading under the same header. */}
					{/* A moment stands at the foot of its page; a question stands at the top of its own,
					    whether or not it has anything under it to answer. */}
					<Rise className={isMoment ? 'flex-1 justify-end' : undefined}>
						{/* A description belongs to the question above it, so it sits nearer to that than
						    to whatever comes next. */}
						{/* Indented to the note's own inset, so the question and the field it is asking
						    about start on one line. Left at the page's edge the two sat sixteen points
						    apart, which is the card's padding showing through as a jog between a label
						    and the thing it labels. */}
						<VStack gap={isMoment ? 8 : 6} className={isMoment ? undefined : 'px-4'}>
							{/* The display face is set tight by default, which is right for one line and
							    cramped over two; a question that wraps needs the room. Both are held
							    short of the page's width so they break into a column rather than running
							    the full measure: the question narrower than the line under it, so the
							    pair reads as a heading and its note. */}
							<Header
								variant={isMoment ? 'h1' : 'h3'}
								className={isMoment ? undefined : 'leading-[32px] max-w-[86%]'}
							>
								{title}
							</Header>
							{!!description && (
								<Text
									size="body0"
									tone="soft"
									className={isMoment ? undefined : 'max-w-[86%]'}
								>
									{description}
								</Text>
							)}
						</VStack>
					</Rise>
					{/* The scroll runs to the note's own edges and passes under glass at them, so a row
					    half out of sight reads as more to come rather than as one cut in two. The note
					    carries no padding of its own and the content carries it instead: padding under
					    the glass is a second band of plain colour below the first, and the two read as
					    one edge twice the depth. */}
					{children && (
						<Note tone={surface} padded={false} className="flex-1">
							<View className="flex-1">
								<Animated.ScrollView
									className="flex-1"
									keyboardShouldPersistTaps="handled"
									showsVerticalScrollIndicator={false}
									scrollEventThrottle={16}
									contentContainerStyle={{ padding: 16 }}
									onScroll={overflow.onScroll}
									onLayout={(event) => overflow.onLayout(event.nativeEvent.layout.height)}
									onContentSizeChange={(_, height) => overflow.onContent(height)}
								>
									{children}
								</Animated.ScrollView>
								<EdgeGlass
									colour={colors[surface === 'note' ? 'note' : 'blush']}
									edge="top"
									style={overflow.top}
								/>
								<EdgeGlass
									colour={colors[surface === 'note' ? 'note' : 'blush']}
									edge="bottom"
									style={overflow.bottom}
								/>
							</View>
						</Note>
					)}
					{cta ? <View className="pb-2">{cta}</View> : null}
				</VStack>
			</PageShell>
		</View>
	)
}
